import type { NoteLike, ApplicationNoteFilter, AdditionalFileSelectionResult } from '../types/utils'
/** Notes tab helpers (Angular note-list parity). */



/** Note body used for pattern checks (Angular only reads `note.notes`). */
export function getNoteBodyText(note: NoteLike | null | undefined): string {
  if (!note) return ""
  const text = note.notes ?? note.note ?? note.note_text ?? ""
  return typeof text === "string" ? text : String(text)
}

/** APPLICATION system-note content patterns (Angular `filterNotes`). */
const APPLICATION_SYSTEM_NOTE_STARTS_WITH: ReadonlyArray<string> = [
  "Email sent to",
  "Lease date update",
  "Risk Rating changed from:",
  "Risk Rating changed from -",
  "Email Change:",
  "Applicant #",
  "Email swap",
  "Lease Start Moved",
  "Separate Application :",
  "Link Application :",
  "Remove Application :",
  "Rent Changed -",
  "Phone Number swap",
  "Update Applicant status",
  "Switched",
  "[AUDIT]",
]

const APPLICATION_SYSTEM_NOTE_INCLUDES: ReadonlyArray<string> = [
  "Checkin by",
  "Checkout by",
  "Checkout transfered to",
  "Denied reason -",
  "was attempted and needs to be reviewed by",
  "Document uploaded:",
  "resubmitted without any application information",
  "Application moved from company",
  "transferred from company",
]

const APPLICATION_SYSTEM_NOTE_EXACT: ReadonlyArray<string> = [
  "Updated Property.",
  "Updated About Yourself<br/>",
  "Updated Income and Employement",
]

/** System vs user note — flags, name, impersonated_by, then content patterns (Angular). */
export function classifyNote(note: NoteLike | null | undefined): "system" | "user" {
  if (!note) return "user"
  const flag = note.is_system_note
  if (flag === "1" || flag === 1 || flag === true) return "system"
  if (typeof note.type === "string" && note.type.toLowerCase() === "system") return "system"
  if (typeof note.note_type === "string" && note.note_type.toUpperCase() === "SYSTEM") return "system"

  if (typeof note.name === "string" && note.name === "System") return "system"

  if (note.impersonated_by != null && note.impersonated_by !== "") return "system"

  const text = getNoteBodyText(note)
  if (text.length === 0) return "user"

  if (APPLICATION_SYSTEM_NOTE_EXACT.includes(text)) return "system"
  if (APPLICATION_SYSTEM_NOTE_STARTS_WITH.some((p) => text.startsWith(p))) return "system"
  if (APPLICATION_SYSTEM_NOTE_INCLUDES.some((p) => text.includes(p))) return "system"

  return "user"
}

/** APPLICATION `filterNotes('system')` branch — includes `Document uploaded:`. */
export function matchesApplicationSystemNoteFilter(
  note: NoteLike | null | undefined,
): boolean {
  if (!note) return false
  const text = getNoteBodyText(note)
  if (!text) return false

  if (note.name === "System") return true
  if (
    text.startsWith("Email sent to") ||
    text.startsWith("Lease date update") ||
    text.includes("Checkin by") ||
    text.includes("Checkout by") ||
    text.includes("Checkout transfered to") ||
    text.includes("Denied reason -") ||
    text.startsWith("Risk Rating changed from:") ||
    text.startsWith("Email Change:") ||
    text.startsWith("Applicant #") ||
    text.startsWith("Email swap") ||
    text.startsWith("Lease Start Moved") ||
    note.impersonated_by != null && note.impersonated_by !== "" ||
    text.startsWith("Separate Application :") ||
    text.startsWith("Link Application :") ||
    text.startsWith("Remove Application :") ||
    text.startsWith("Rent Changed -") ||
    text.includes("was attempted and needs to be reviewed by") ||
    text.startsWith("Phone Number swap") ||
    text.startsWith("Update Applicant status") ||
    text.includes("Document uploaded:") ||
    text.includes("resubmitted without any application information") ||
    text.includes("Application moved from company") ||
    APPLICATION_SYSTEM_NOTE_EXACT.includes(text) ||
    text.startsWith("Switched")
  ) {
    return true
  }

  if (text.startsWith("Underwriting Verified Income")) return true

  return false
}

/** APPLICATION `filterNotes('user')` — does not exclude `Document uploaded:`. */
export function matchesApplicationUserNoteFilter(
  note: NoteLike | null | undefined,
): boolean {
  if (!note) return false
  if (note.name === "System") return false

  const text = getNoteBodyText(note)

  if (
    text.startsWith("Email sent to") ||
    text.startsWith("Lease date update") ||
    text.includes("Checkin by") ||
    text.includes("Checkout by") ||
    text.includes("Checkout transfered to") ||
    text.includes("Denied reason -") ||
    text.startsWith("Risk Rating changed from:") ||
    text.startsWith("Email Change:") ||
    text.startsWith("Applicant #") ||
    text.startsWith("Email swap") ||
    text.startsWith("Lease Start Moved") ||
    (note.impersonated_by != null && note.impersonated_by !== "") ||
    text.startsWith("Separate Application :") ||
    text.startsWith("Link Application :") ||
    text.startsWith("Remove Application :") ||
    text.startsWith("Rent Changed -") ||
    text.includes("was attempted and needs to be reviewed by") ||
    text.startsWith("Phone Number swap") ||
    text.includes("resubmitted without any application information") ||
    text.startsWith("Update Applicant status") ||
    text.includes("Application moved from company") ||
    APPLICATION_SYSTEM_NOTE_EXACT.includes(text) ||
    text.startsWith("Switched")
  ) {
    return false
  }

  return true
}

/** Radio filter for APPLICATION recent notes (Angular `filterNotes`). */
export function filterApplicationNote(
  note: NoteLike | null | undefined,
  filter: ApplicationNoteFilter,
): boolean {
  if (!note) return false
  if (filter === "all") return true
  if (filter === "system") return matchesApplicationSystemNoteFilter(note)
  return matchesApplicationUserNoteFilter(note)
}

/** Normalize API `created_at` to unix seconds (supports unix sec/ms and ISO strings). */
export function noteCreatedAtToUnixSeconds(createdRaw: unknown): number | null {
  if (createdRaw == null || createdRaw === "") return null
  if (typeof createdRaw === "number") {
    if (!Number.isFinite(createdRaw)) return null
    return createdRaw > 1e12 ? Math.floor(createdRaw / 1000) : Math.floor(createdRaw)
  }
  const s = String(createdRaw).trim()
  if (!s) return null
  if (/^\d{10,13}$/.test(s)) {
    const n = Number(s)
    return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n)
  }
  const parsed = Date.parse(s)
  if (!Number.isFinite(parsed)) return null
  return Math.floor(parsed / 1000)
}

/** Editable within 5 min, Liberty role, own note, not system (Angular refreshNotes). */
export function canEditNote(
  note: NoteLike & { created_at?: number | string | null; fk_user_id?: number | string | null },
  ctx: {
    isLibertyRole: boolean
    currentUserId: number | string | null | undefined
    now?: number
  },
): boolean {
  if (!note) return false
  if (!ctx.isLibertyRole) return false
  if (ctx.currentUserId == null || String(ctx.currentUserId) === "") return false
  if (String(note.fk_user_id ?? "") !== String(ctx.currentUserId)) return false
  if (classifyNote(note) === "system") return false

  const createdSec = noteCreatedAtToUnixSeconds(note.created_at)
  if (createdSec == null) return false

  const nowSec = Math.floor((ctx.now ?? Date.now()) / 1000)
  return nowSec - createdSec < 5 * 60
}

/** Pull the API note id off a row. The server returns either `notes_id` or `id`. */
export function getNoteRowId(
  note: { notes_id?: string | number | null; id?: string | number | null } | null | undefined,
): string | number | null {
  if (!note) return null
  if (note.notes_id != null && String(note.notes_id) !== "") return note.notes_id
  if (note.id != null && String(note.id) !== "") return note.id
  return null
}

const ONE_MB = 1024 * 1024
const TEN_MB = 10 * ONE_MB


/** Additional-files upload: 10 MB cap (error), >1 MB warning. */
export function validateAdditionalFileSelection(
  files: ReadonlyArray<{ name: string; size: number }>,
  options?: { maxBytes?: number; warnBytes?: number },
): AdditionalFileSelectionResult {
  const maxBytes = options?.maxBytes ?? TEN_MB
  const warnBytes = options?.warnBytes ?? ONE_MB

  if (!files || files.length === 0) {
    return { ok: false, errors: ["Please select files to upload"], warnings: [] }
  }

  const errors: string[] = []
  const warnings: AdditionalFileSelectionResult["warnings"] = []
  for (const file of files) {
    if (file.size > maxBytes) {
      errors.push("File Size Exceeds Allowable Limit of 10 MB.")
      // Single error message reused (matches existing UX); do not push duplicates.
      break
    }
  }
  if (errors.length === 0) {
    for (const file of files) {
      if (file.size > warnBytes) {
        warnings.push({
          name: file.name,
          sizeBytes: file.size,
          sizeMb: Number((file.size / ONE_MB).toFixed(2)),
        })
      }
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
  }
}

export function formatLargeFileWarningList(
  warnings: AdditionalFileSelectionResult["warnings"],
): string {
  return warnings.map((w) => `${w.name} — ${w.sizeMb.toFixed(2)} MB`).join("\n")
}

export type { AdditionalFileSelectionResult, NoteLike } from '../types/utils'

